import { zodResolver } from '@hookform/resolvers/zod';
import { CSSProperties, FC } from 'react';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Row } from '@tempmee/react';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { FormErrorBanner, useFormSubmit } from '~/components/shared/forms';
import { IProfessionOption } from './useSelectProfessionType';
import { ProfessionTypeCard } from './ProfessionTypeCard';

// ConfirmPopup overrides — ConfirmPopup is a JS component with no DS equivalents for these props.
// Space below the description text before the profession type card grid.
const POPUP_DESCRIPTION_STYLE: CSSProperties = { marginBottom: 24, width: 460 };
// Minimum width to accommodate 2 profession type cards (≈200px each) plus gap and padding.
const POPUP_MODAL_STYLE: CSSProperties = { minWidth: 614 };

interface ISelectProfessionTypePopupProps {
  professions: IProfessionOption[];
  onContinue: (professionCode: string) => void;
  onClose: () => void;
}

const schema = z.object({
  professionCode: z.string().min(1, 'Please select a profession type.'),
});

type IFormValues = z.infer<typeof schema>;

const SelectProfessionTypeForm: FC<ISelectProfessionTypePopupProps> = ({
  professions,
  onContinue,
  onClose,
}) => {
  const { field } = useController<IFormValues, 'professionCode'>({
    name: 'professionCode',
  });

  const { submit, bannerVisible, bannerRef } = useFormSubmit<IFormValues>({
    onValid: ({ professionCode }) => onContinue(professionCode),
    scrollOnInvalid: false,
  });

  return (
    <ConfirmPopup
      title="Select a Professional Type"
      description={'Start by selecting the professional type, then continue to the shift details'}
      showClosePopupIcon
      closePopup={onClose}
      leftButtonText="Go Back"
      leftButtonAction={onClose}
      rightButtonText="Continue"
      rightButtonAction={submit}
      descriptionStyle={POPUP_DESCRIPTION_STYLE}
      dismissiblePopup={false}
      modalStyle={POPUP_MODAL_STYLE}
    >
      <FormErrorBanner
        ref={bannerRef}
        visible={bannerVisible}
        message="Please select a profession type to continue."
      />
      <Row justify="center" wrap="wrap" style={{ gap: 10, marginBottom: 32, marginTop: bannerVisible ? 12 : 0 }}>
        {professions.map((profession) => (
          <ProfessionTypeCard
            key={profession.code}
            icon={profession.icon}
            label={profession.label}
            isSelected={field.value === profession.code}
            onClick={() =>
              field.onChange(field.value === profession.code ? '' : profession.code)
            }
          />
        ))}
      </Row>
    </ConfirmPopup>
  );
};

export const SelectProfessionTypePopup: FC<ISelectProfessionTypePopupProps> = (props) => {
  const methods = useForm<IFormValues>({
    defaultValues: { professionCode: '' },
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...methods}>
      <SelectProfessionTypeForm {...props} />
    </FormProvider>
  );
};
